// Client Health Screening, Informed Consent & Liability Waiver — content config.
// Single-page form: client info, YES/NO screening, explanation fields,
// read-only legal sections, touch consent, acknowledgement, and signatures.

export const CLIENT_FIELDS = [
  { key: 'name', label: 'Client Name', type: 'text' },
  { key: 'dob', label: 'Date of Birth', type: 'text' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'emergency_contact', label: 'Emergency Contact', type: 'text' },
  { key: 'emergency_phone', label: 'Emergency Contact Phone', type: 'text' },
];

export const SCREENING_INTRO =
  'Please answer YES or NO to the following questions. If you are unsure, please answer YES and provide additional information.';

// Screening questions. `options` defaults to Yes/No.
export const SCREENING = [
  {
    group: 'Cardiovascular & General Health',
    items: [
      { key: 'q1', label: 'Has a doctor or healthcare provider ever told you that you have a heart or cardiovascular condition that may affect your ability to exercise or participate in physical activity?' },
      { key: 'q2', label: 'Have you experienced chest pain, chest pressure, unusual shortness of breath, dizziness, fainting, or loss of consciousness during physical activity or at rest?' },
      { key: 'q3', label: 'Have you been diagnosed with or treated for high blood pressure, stroke, heart disease, diabetes, respiratory disease, or another condition that may be affected by physical activity?' },
    ],
  },
  {
    group: 'Injuries, Pain & Physical Limitations',
    items: [
      { key: 'q4', label: 'Do you currently have an injury, pain, swelling, joint problem, back or neck condition, or other musculoskeletal issue that could affect your ability to move or exercise?' },
      { key: 'q5', label: 'Have you had surgery, a significant injury, accident, hospitalization, or major medical treatment within the past 12 months?' },
      { key: 'q6', label: 'Do you have any ongoing neurological, balance, dizziness, mobility, bone-density, spinal, joint, or connective-tissue condition that may require exercise modification?' },
    ],
  },
  {
    group: 'Medical Considerations',
    items: [
      { key: 'q7', label: 'Are you currently under the care of a physician or other healthcare professional for a condition that may affect your participation?' },
      { key: 'q8', label: 'Has a healthcare professional advised you to avoid or modify certain types of exercise, movement, positions, breathing practices, heat exposure, or other physical activities?' },
      { key: 'q9', label: 'Are you currently taking medication that may affect heart rate, blood pressure, balance, alertness, exercise tolerance, hydration, or your response to physical activity?' },
      { key: 'q10', label: 'Are you pregnant, recently postpartum, or aware of another circumstance that may require modification of physical activity?', options: ['Yes', 'No', 'N/A'] },
      { key: 'q11', label: 'Is there any other medical condition, physical limitation, injury, symptom, or health concern that I should know about before you participate?' },
    ],
  },
];

export const EXPLAIN_FIELDS = [
  { key: 'explain_yes', label: 'If you answered YES to any question, please explain:' },
  { key: 'explain_injuries', label: 'Current injuries, pain or movement limitations:' },
  { key: 'explain_surgeries', label: 'Relevant surgeries or significant medical history:' },
  { key: 'explain_medications', label: 'Current medications or other information relevant to participation:' },
];

// Read-only legal sections shown to the client and printed to the PDF.
export const SECTIONS = [
  {
    num: '2', title: 'Client Responsibility & Health Changes',
    body: [
      'I understand that this health screening is intended to help identify circumstances that may require modification, additional assessment, or consultation with an appropriate healthcare professional.',
      'I agree to provide accurate and complete information regarding my health to the best of my knowledge.',
      'I understand that my health and physical condition may change over time. I agree to inform Grounded Wellness with Jill of any significant change, new injury, illness, surgery, pregnancy, medication change, new symptoms, or medical advice that may affect my participation.',
      'I understand that I may be asked to obtain medical or other professional clearance before beginning or continuing certain activities when appropriate.',
    ],
  },
  {
    num: '3', title: 'Nature of Services',
    body: [
      'I understand that services provided by Grounded Wellness with Jill / Jill Bennett may include, depending upon the service, class, session or program:',
    ],
    list: [
      'Personal training and restorative movement',
      'Mobility, flexibility and corrective exercise',
      'Fascial movement and Fascial Maneuvers',
      'Functional movement and movement education',
      'Strength, balance and conditioning exercises',
      'Breathwork and breathing exercises',
      'Meditation, mindfulness and relaxation practices',
      'Nervous-system regulation and stress-management practices',
      'Lifestyle and holistic health coaching',
      'Nutrition and general wellness education',
      'Online, recorded, virtual, outdoor and in-person programs',
      "Other complementary wellness practices within the practitioner's training and scope",
    ],
    after: ['I understand that not every service listed above will necessarily be included in my program.'],
  },
  {
    num: '4', title: 'Informed Consent',
    body: [
      'I understand that participation in physical activity and wellness practices involves inherent risks.',
      'These may include, but are not limited to: muscle soreness, strains, sprains, falls, dizziness, changes in blood pressure or heart rate, aggravation of an existing condition or injury, fatigue, emotional responses, and other unforeseen physical reactions or injuries.',
      'I understand that some practices may produce temporary physical sensations or emotional responses as I become more aware of my body, breathing, movement patterns or stress responses.',
      'I understand that I am responsible for communicating discomfort, unusual symptoms, pain, dizziness, shortness of breath, numbness, weakness, distress, or any other concerning response during a session.',
      'I understand that I may stop, decline or modify any exercise, movement, technique or practice at any time.',
    ],
  },
  {
    num: '5', title: 'Wellness Coaching & Scope of Practice',
    body: [
      'I understand that wellness coaching, movement education, nutrition education, breathwork, meditation and other complementary wellness services are intended for education and general wellness support.',
      'They are not a substitute for medical diagnosis, medical treatment, psychotherapy, physiotherapy, emergency medical care, or other licensed healthcare services when those services are required.',
      'I understand that Jill Bennett does not diagnose disease, prescribe medication, or instruct me to discontinue medical treatment or prescribed medication.',
      'Any information concerning nutrition, supplements, lifestyle practices, movement, stress management or wellness is educational in nature and should be considered alongside advice from my qualified healthcare providers where appropriate.',
      'I remain responsible for decisions concerning my own healthcare and participation.',
    ],
  },
  {
    num: '6', title: 'Assumption of Risk',
    body: [
      'I voluntarily choose to participate in services provided by Grounded Wellness with Jill.',
      'I understand that even with appropriate instruction, screening, modifications and reasonable care, all risks associated with physical activity and wellness practices cannot be eliminated.',
      'I knowingly and voluntarily accept the ordinary and inherent risks associated with my participation.',
    ],
  },
  {
    num: '7', title: 'Release & Waiver of Liability',
    body: [
      'To the fullest extent permitted by applicable law, I voluntarily assume the risks associated with participation and agree to release and hold harmless Grounded Wellness with Jill, Jill Bennett, its instructors, contractors, representatives, and participating facilities from claims arising from the ordinary and inherent risks of my voluntary participation.',
      'This waiver is not intended to exclude or limit any liability that cannot lawfully be excluded or limited.',
      'I understand that I remain responsible for following instructions, working within my abilities, communicating relevant health information, and stopping an activity when I feel unable to continue safely.',
    ],
  },
  {
    num: '8', title: 'In-Person, Online, Recorded & Unsupervised Participation',
    body: [
      'I understand that some sessions or programs may be delivered live online, through prerecorded video, through written instruction, or practiced independently.',
      'When participating remotely or independently, I am responsible for:',
    ],
    list: [
      'Choosing a safe and appropriate space',
      'Removing obstacles and hazards',
      'Using appropriate equipment',
      'Following instructions and suggested modifications',
      'Working within my current abilities',
      'Stopping if I experience pain, dizziness, unusual shortness of breath or other concerning symptoms',
      'Seeking appropriate medical attention when necessary',
    ],
    after: ['I understand that the instructor may not be able to observe or correct my movements when I participate through recordings or outside a supervised session.'],
  },
];

export const TOUCH_CONSENT = {
  num: '9', title: 'Touch & Hands-On Cueing',
  body: [
    'Some in-person movement sessions may involve hands-on positioning or tactile cueing to assist with body awareness, alignment or movement.',
    'My consent will be requested where appropriate.',
    'I understand that I may decline hands-on contact at any time without needing to provide a reason.',
  ],
  question: 'I am comfortable being offered hands-on movement cueing:',
  options: ['Yes', 'No', 'Please Ask Each Time'],
};

export const AFTER_TOUCH_SECTIONS = [
  {
    num: '10', title: 'Emergency Care',
    body: [
      'If I become ill or injured during an in-person session and am unable to communicate my wishes, I authorize reasonable steps to obtain emergency assistance on my behalf.',
      'I understand that I am responsible for any medical or emergency-care expenses incurred.',
    ],
  },
  {
    num: '11', title: 'Privacy',
    body: [
      'Information provided through this form is collected for client screening, safety, program planning and wellness-coaching purposes.',
      'I understand that my personal and health information will be treated as confidential and will not ordinarily be shared with third parties without my consent, except where disclosure is required or permitted by law or necessary in an emergency.',
    ],
  },
];

export const ACKNOWLEDGEMENT = {
  num: '12', title: 'Client Acknowledgement',
  intro: 'By signing below, I confirm that:',
  items: [
    'I have read and understood this document.',
    'I have had the opportunity to ask questions.',
    'The health information I have provided is accurate to the best of my knowledge.',
    'I understand the nature and limitations of the services being provided.',
    'I understand that participation involves inherent risks.',
    'I voluntarily choose to participate.',
    'I understand that I may stop or decline any activity at any time.',
    'I agree to inform the practitioner of relevant changes to my health.',
    'I understand that this document does not replace appropriate medical care or medical advice.',
  ],
};

export const GUARDIAN_TEXT =
  "I am the parent or legal guardian of the participant named above. I have read and understood this document and consent to the participant's involvement in the applicable services.";

// key -> label lookup (for PDF)
export const WAIVER_LABELS = (() => {
  const acc = {};
  CLIENT_FIELDS.forEach((f) => { acc[f.key] = f.label; });
  SCREENING.forEach((g) => g.items.forEach((i) => { acc[i.key] = i.label; }));
  EXPLAIN_FIELDS.forEach((f) => { acc[f.key] = f.label; });
  return acc;
})();
